// src/services/apiService.js
import {
  TENANT_ID,
  API_CONDOMINIO_BASE_URL,
  API_ENCOMENDA_BASE_URL,
  API_LOG_ENABLED,
  AUTH0_DOMAIN,
  AUTH0_CLIENT_ID,
} from '@/lib/config';
import { dataURLtoFile } from '@/utils/imageUtils';
import { logger } from '@/lib/logger/index.js';
import { getAccessToken } from '@/lib/authToken';
import { fetchWithRetry } from '@/lib/fetchWithRetry';

async function apiFetch(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase();

  const token = await getAccessToken();
  const headers = { ...(options.headers || {}) };
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }
  const requestOptions = { ...options, headers };

  if (API_LOG_ENABLED) {
    logger.debug(`[API] → ${method} ${url}`);
  }
  const response = await fetch(url, requestOptions);
  if (API_LOG_ENABLED) {
    logger.debug(`[API] ${response.ok ? '✓' : '✗'} ${response.status} ${method} ${url}`);
  }
  return response;
}

/**
 * Busca os detalhes de uma única encomenda pelo seu ID.
 * @param {string} encomendaId - O ID da encomenda (rowKey).
 * @returns {Promise<object>} - O objeto com os detalhes da encomenda.
 */
export async function fetchEncomendaById(encomendaId) {
  const url = `${API_ENCOMENDA_BASE_URL}/encomendas/${TENANT_ID}/encomenda/${encomendaId}`;
  const response = await apiFetch(url);

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = errorData?.message || `Erro ao buscar detalhes da encomenda: ${response.status}`;
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Atribui uma unidade a uma encomenda específica.
 * @param {string} condominoId - O ID do condomínio (ex: 'solaritac' ).
 * @param {string} encomendaId - O ID da encomenda (rowKey).
 * @param {string} codigoUnidade - O código da unidade a ser atribuída.
 * @returns {Promise<boolean>} - Retorna true se for bem-sucedido.
 * @throws {Error} - Lança um erro se a chamada à API falhar.
 */
export async function assignUnitToEncomenda(condominoId, encomendaId, codigoUnidade) {
  const url = `${API_ENCOMENDA_BASE_URL}/encomendas/${condominoId}/encomenda/${encomendaId}/unidade/${codigoUnidade}`;

  const response = await apiFetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      // Futuramente, adicionaremos o token de autenticação aqui:
      // 'Authorization': `Bearer ${token}`
    },
  });

  if (!response.ok) {
    // Tenta ler uma mensagem de erro do corpo da resposta, se houver
    const errorData = await response.json().catch(() => null);
    const errorMessage = errorData?.message || `Erro na API: ${response.status} ${response.statusText}`;
    throw new Error(errorMessage);
  }

  // O endpoint PUT pode não retornar um corpo, então apenas o status 2xx é suficiente
  return true;
}

/**
 * Atribui uma unidade a uma encomenda específica.
 * @param {string} condominoId - O ID do condomínio (ex: 'solaritac' ).
 * @param {string} encomendaId - O ID da encomenda (rowKey).
 * @param {string} codigoUnidade - O código da unidade a ser atribuída.
 * @returns {Promise<boolean>} - Retorna true se for bem-sucedido.
 * @throws {Error} - Lança um erro se a chamada à API falhar.
 */
export async function assignUnitToEncomendaWithDestinatario(condominoId, encomendaId, codigoUnidade, moradorId) {
  const url = `${API_ENCOMENDA_BASE_URL}/encomendas/${condominoId}/encomenda/${encomendaId}/unidade/${codigoUnidade}/destinatario/${moradorId}`;

  const response = await apiFetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      // Futuramente, adicionaremos o token de autenticação aqui:
      // 'Authorization': `Bearer ${token}`
    },
  });

  if (!response.ok) {
    // Tenta ler uma mensagem de erro do corpo da resposta, se houver
    const errorData = await response.json().catch(() => null);
    const errorMessage = errorData?.message || `Erro na API: ${response.status} ${response.statusText}`;
    throw new Error(errorMessage);
  }

  // O endpoint PUT pode não retornar um corpo, então apenas o status 2xx é suficiente
  return true;
}


/**
 * Registra uma nova encomenda enviando a imagem da etiqueta.
 * @param {string} condominoId - O ID do condomínio.
 * @param {string} imageDataUrl - A imagem da etiqueta em formato Data URL.
 * @returns {Promise<boolean>} - Retorna true se for bem-sucedido.
 * @throws {Error} - Lança um erro se a chamada à API falhar.
 */
export async function registerEncomenda(condominoId, imageDataUrl) {
  const url = `${API_ENCOMENDA_BASE_URL}/encomendas/${condominoId}/queue`;

  // 1. Converte a imagem Data URL para um objeto File
  const imageFile = dataURLtoFile(imageDataUrl, 'encomenda.jpeg');

  // 2. Cria um objeto FormData
  const formData = new FormData();
  formData.append('file', imageFile); // O nome 'file' deve corresponder ao esperado pela API

  // 3. Faz a chamada fetch com o FormData
  const response = await apiFetch(url, {
    method: 'POST',
    // NÃO defina o 'Content-Type' manualmente! O navegador fará isso
    // automaticamente para multipart/form-data, incluindo o 'boundary' correto.
    body: formData,
    // Headers de autenticação serão adicionados aqui no futuro
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = errorData?.message || `Erro ao registrar encomenda: ${response.status} ${response.statusText}`;
    throw new Error(errorMessage);
  }

  logger.transaction('Encomenda Registrada', { condominoId });

  return true;
}

/**
 * Cancela uma encomenda que ainda não tem condômino/unidade identificado.
 * @param {string} condominoId - O ID do condomínio (tenant).
 * @param {string} encomendaId - O ID da encomenda (rowKey).
 * @param {{motivo: string, observacao?: string}} payload - Motivo do cancelamento e observação opcional.
 * @returns {Promise<object>} - A encomenda atualizada, com os dados de cancelamento.
 * @throws {Error} - Lança um erro (com a mensagem do back-end) se a chamada falhar.
 */
export async function cancelarEncomenda(condominoId, encomendaId, { motivo, observacao } = {}) {
  const url = `${API_ENCOMENDA_BASE_URL}/encomendas/${condominoId}/encomenda/${encomendaId}/cancelar`;

  const response = await apiFetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ motivo, observacao: observacao || null }),
  });

  if (!response.ok) {
    // 400 vem com { message }, 404 pode vir como texto simples — tenta ambos.
    const rawBody = await response.text().catch(() => '');
    let errorMessage = rawBody;
    try {
      const errorData = JSON.parse(rawBody);
      errorMessage = errorData?.message || rawBody;
    } catch {
      // corpo já é texto simples, mantém como está
    }
    throw new Error(errorMessage || `Erro ao cancelar encomenda: ${response.status} ${response.statusText}`);
  }

  logger.transaction('Encomenda Cancelada', { condominoId, encomendaId, motivo });

  return response.json();
}

export function getImagemEncomendaUrl(imageId) {
  if (!imageId) return null;
  if (typeof imageId === 'string' && (imageId.startsWith('http://') || imageId.startsWith('https://') || imageId.startsWith('data:'))) {
    return imageId;
  }
  return `${API_ENCOMENDA_BASE_URL}/encomendas/${TENANT_ID}/image/${imageId}`;
}

/**
 * Busca os detalhes de uma encomenda a partir da URL de retirada do QRCode.
 * @param {string} qrCodeUrl - A URL completa lida do QRCode.
 * @returns {Promise<object>} - O objeto com os detalhes da encomenda.
 * @throws {Error} - Lança um erro se a chamada à API falhar.
 */
export async function getEncomendaDetailsFromQrUrl(qrCodeUrl) {
  qrCodeUrl = qrCodeUrl.replace('http://','https://');
  
  // A URL já é o endpoint completo, não precisamos do API_ENCOMENDA_BASE_URL
  const response = await apiFetch(qrCodeUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Id': TENANT_ID,
      //'Authorization': `Bearer ${token}`
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = errorData?.message || `Erro ao buscar detalhes do QRCode: ${response.status} ${response.statusText}`;
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Confirma a retirada de uma encomenda.
 * @param {string} token - O Token de Retirada da encomenda (rowKey).
 * @returns {Promise<boolean>} - Retorna true se for bem-sucedido.
 * @throws {Error} - Lança um erro se a chamada à API falhar.
 */
export async function confirmarRetirada(token) {
  // O endpoint é construído com base no ID da encomenda, não no link completo do QR.
  const url = `${API_ENCOMENDA_BASE_URL}/encomendas/${TENANT_ID}/retirada/${token}/confirmar`;

  const response = await apiFetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      // 'Authorization': `Bearer ${token}`
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = errorData?.message || `Erro ao confirmar retirada: ${response.status} ${response.statusText}`;
    throw new Error(errorMessage);
  }

  logger.transaction('Retirada Confirmada Automática', { token });

  return true;
}

/**
 * Confirma a retirada manual de uma encomenda enviando a imagem de evidência.
 * @param {string} encomendaId - O ID da encomenda.
 * @param {string} evidenciaImageDataUrl - A imagem de evidência em formato Data URL.
 * @returns {Promise<boolean>}
 */
export async function confirmarRetiradaComEvidencia(encomendaId, evidenciaImageDataUrl) {
  // CONFIRME ESTE ENDPOINT
  const url = `${API_ENCOMENDA_BASE_URL}/encomendas/${TENANT_ID}/retirada/${encomendaId}/confirmar/manual`;

  const imageFile = dataURLtoFile(evidenciaImageDataUrl, `comprovante_${encomendaId}.jpeg`);
  const formData = new FormData();
  formData.append('file', imageFile);

  const response = await apiFetch(url, {
    method: 'PUT',
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = errorData?.message || `Erro ao confirmar retirada com evidência: ${response.status}`;
    throw new Error(errorMessage);
  }

  logger.transaction('Retirada Manual Confirmada c/ Evidência', { encomendaId });

  return true;
}

/**
 * Retorna a URL completa para a imagem de evidência de retirada.
 * @param {string} imageId - O ID da imagem de evidência.
 * @returns {string|null}
 */
export function getImagemComprovanteUrl(tenant, imageId) {
  if (!imageId) return null;
  // Assumindo que o endpoint segue um padrão similar ao da imagem da encomenda
  // CONFIRME ESTE ENDPOINT
  return `${API_ENCOMENDA_BASE_URL}/encomendas/${tenant}/retirada/evidencia/${imageId}`;
}

/**
 * Busca a lista paginada de encomendas canceladas.
 * @param {string} tenantId - O ID do condomínio (tenant).
 * @param {number} pageSize - Quantidade de itens por página.
 * @param {string|null} continuationToken - Token de continuação da página anterior.
 * @returns {Promise<{items: object[], continuationToken: string|null}>}
 */
export async function fetchEncomendasCanceladas(tenantId, pageSize = 50, continuationToken = null) {
  let url = `${API_ENCOMENDA_BASE_URL}/encomendas/${tenantId}/canceladas?pageSize=${pageSize}`;
  if (continuationToken) url += `&continuationToken=${encodeURIComponent(continuationToken)}`;

  const response = await apiFetch(url);
  if (!response.ok) throw new Error(`Erro ao buscar encomendas canceladas: ${response.status}`);
  return response.json();
}

export async function fetchEncomendasRetiradas(tenantId, dataInicio, dataFim, pageSize = 50, continuationToken = null) {
  const inicio = dataInicio instanceof Date ? dataInicio.toISOString().split('T')[0] : String(dataInicio).split('T')[0];
  const fim    = dataFim instanceof Date ? dataFim.toISOString().split('T')[0] : String(dataFim).split('T')[0];

  let url = `${API_ENCOMENDA_BASE_URL}/encomendas/${tenantId}/retiradas/inicio/${inicio}/fim/${fim}?pageSize=${pageSize}`;
  if (continuationToken) url += `&continuationToken=${encodeURIComponent(continuationToken)}`;

  const response = await apiFetch(url);
  if (!response.ok) throw new Error(`Erro ao buscar retiradas: ${response.status}`);
  return response.json();
}

/**
 * Busca o histórico de encomendas de uma unidade específica.
 * @param {string} tenantId - O ID do condomínio (tenant).
 * @param {string} codigoUnidade - O código da unidade (ex: '101', '202A').
 * @param {{ pageSize?: number, continuationToken?: string }} [options]
 * @returns {Promise<{items: object[], continuationToken: string|null}>}
 */
export async function fetchEncomendasPorUnidade(tenantId, codigoUnidade, { pageSize = 50, continuationToken = null } = {}) {
  let url = `${API_ENCOMENDA_BASE_URL}/encomendas/${tenantId}/unidade/${encodeURIComponent(codigoUnidade)}?pageSize=${pageSize}`;
  if (continuationToken) url += `&continuationToken=${encodeURIComponent(continuationToken)}`;

  const response = await apiFetch(url);
  if (!response.ok) throw new Error(`Erro ao buscar histórico de encomendas da unidade: ${response.status}`);
  return response.json();
}

/**
 * Busca o histórico de encomendas de um morador pelo telefone.
 * @param {string} tenantId - O ID do condomínio (tenant).
 * @param {string} telefone - O telefone do morador.
 * @param {{ pageSize?: number, continuationToken?: string }} [options]
 * @returns {Promise<{items: object[], continuationToken: string|null}>}
 */
export async function fetchEncomendasPorMoradorTelefone(tenantId, telefone, { pageSize = 50, continuationToken = null } = {}) {
  const cleanPhone = String(telefone).replace(/\D/g, '') || telefone;
  let url = `${API_ENCOMENDA_BASE_URL}/encomendas/${tenantId}/morador/${encodeURIComponent(cleanPhone)}?pageSize=${pageSize}`;
  if (continuationToken) url += `&continuationToken=${encodeURIComponent(continuationToken)}`;

  const response = await apiFetch(url);
  if (!response.ok) throw new Error(`Erro ao buscar histórico de encomendas do morador: ${response.status}`);
  return response.json();
}

/**
 * Busca o histórico de encomendas de um morador ou unidade.
 * Dá prioridade ao telefone do morador e, caso não possua telefone, busca pela unidade.
 * @param {string} tenantId - O ID do condomínio (tenant).
 * @param {string|{ telefone?: string, codigoUnidade?: string, pageSize?: number, continuationToken?: string }} target - Identificador ou objeto com parâmetros.
 * @param {{ telefone?: string, codigoUnidade?: string, pageSize?: number, continuationToken?: string }} [options]
 * @returns {Promise<{items: object[], continuationToken: string|null}>}
 */
export async function fetchEncomendasPorMorador(tenantId, target, options = {}) {
  let telefone = options.telefone;
  let codigoUnidade = options.codigoUnidade;
  let pageSize = options.pageSize || 50;
  let continuationToken = options.continuationToken || null;

  if (typeof target === 'object' && target !== null) {
    telefone = target.telefone || telefone;
    codigoUnidade = target.codigoUnidade || codigoUnidade;
    pageSize = target.pageSize || pageSize;
    continuationToken = target.continuationToken || continuationToken;
  } else if (typeof target === 'string') {
    if (/^\d{8,15}$/.test(target.replace(/\D/g, '')) || target.includes('@') || target.startsWith('+')) {
      telefone = target;
    } else {
      codigoUnidade = codigoUnidade || target;
    }
  }

  if (telefone) {
    return fetchEncomendasPorMoradorTelefone(tenantId, telefone, { pageSize, continuationToken });
  }

  if (codigoUnidade) {
    return fetchEncomendasPorUnidade(tenantId, codigoUnidade, { pageSize, continuationToken });
  }

  throw new Error('É necessário informar o telefone do morador ou o código da unidade para buscar as encomendas.');
}



/**
 * Cria uma nova solicitação de autorização de acesso.
 * @param {object} autorizacaoData - O objeto JSON com os dados da autorização.
 * @returns {Promise<object>} - Os dados da autorização criada pela API.
 * @throws {Error} - Lança um erro se a chamada à API falhar.
 */
export async function createAutorizacao(autorizacaoData) {
  // O endpoint para criar autorizações
  const url = `${API_CONDOMINIO_BASE_URL}/Autorizacoes`;

  
  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Futuramente, adicionaremos o token de autenticação aqui
    },
    body: JSON.stringify(autorizacaoData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    const errorMessage = errorData.message || `Erro ao criar autorização: ${response.status}`;
    throw new Error(errorMessage);
  }

  return response.json(); // Retorna a resposta da API (a autorização criada)
}

/**
 * Realiza o check-in de uma autorização anexando a foto a um documento existente.
 * @param {string} autorizacaoId - O ID da autorização.
 * @param {string} documentoId - O ID do documento (CPF) ao qual a foto será anexada.
 * @param {string} fotoDocumentoDataUrl - A imagem do documento em formato Data URL.
 * @returns {Promise<object>} - O objeto de resposta da API de check-in.
 */
export async function registrarEntradaAutorizacao(autorizacaoId, documentoId, fotoDocumentoDataUrl) {
  const url = `${API_CONDOMINIO_BASE_URL}/Autorizacoes/Condominio/${TENANT_ID}/Autorizacao/${autorizacaoId}/CheckIn/Documento/${documentoId}`;


  
  const formData = new FormData();
  const imageFile = dataURLtoFile(fotoDocumentoDataUrl, `documento_${documentoId}.jpeg`);
  formData.append('documento', imageFile);

  const response = await apiFetch(url, {
    method: 'PUT',
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: `Erro no servidor: ${response.statusText}` }));
    const errorMessage = errorData.message || `Falha ao registrar check-in: ${response.status}`;
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Busca os detalhes de uma única autorização pelo seu ID.
 * @param {string} autorizacaoId - O ID da autorização.
 * @returns {Promise<object>} - O objeto com os detalhes da autorização.
 */
export async function fetchAutorizacaoById(autorizacaoId) {
  const url = `${API_CONDOMINIO_BASE_URL}/Autorizacoes/${autorizacaoId}`;
  const response = await apiFetch(url);

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = errorData?.message || `Erro ao buscar detalhes da autorização: ${response.status}`;
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Realiza o checkout (registra a saída) de uma autorização.
 * @param {string} autorizacaoId - O ID da autorização.
 * @returns {Promise<object>}
 */
export async function registrarCheckoutAutorizacao(autorizacaoId) {
  const url = `${API_CONDOMINIO_BASE_URL}/Autorizacoes/Condominio/${TENANT_ID}/Autorizacao/${autorizacaoId}/CheckOut`;

  const response = await apiFetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: `Erro no servidor: ${response.statusText}` }));
    throw new Error(errorData.message || `Falha ao registrar checkout: ${response.status}`);
  }

  logger.transaction('Checkout de Autorização Realizado', { autorizacaoId });

  return response.json();
}

export async function adicionarMorador(codigoUnidade, moradorData) {
  const response = await apiFetch(`${API_CONDOMINIO_BASE_URL}/Condominios/${TENANT_ID}/Unidade/${codigoUnidade}/Morador`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(moradorData),
  });

  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`Falha ao adicionar morador: ${errorData}`);
  }
  return await response.json(); // A API pode retornar o morador criado com ID
}

/**
 * Edita os dados (nome/telefone/tipo) de um morador já existente, sem alterar o vínculo.
 * @param {string} codigoUnidade - O código da unidade.
 * @param {string} moradorId - O ID do morador.
 * @param {object} moradorData - Os dados atualizados do morador.
 * @returns {Promise<object>}
 */
export async function atualizarMorador(codigoUnidade, moradorId, moradorData) {
  const response = await apiFetch(`${API_CONDOMINIO_BASE_URL}/Condominios/${TENANT_ID}/Unidade/${codigoUnidade}/Morador/${moradorId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(moradorData),
  });

  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`Falha ao atualizar morador: ${errorData}`);
  }
  return await response.json();
}

/**
 * Encerra o vínculo de um morador com a unidade (mantém o histórico).
 * @param {string} codigoUnidade - O código da unidade.
 * @param {string} moradorId - O ID do morador.
 * @param {string} [dataFim] - Data de fim do vínculo (yyyy-MM-dd). Padrão: hoje.
 * @returns {Promise<object>}
 */
export async function removerMorador(codigoUnidade, moradorId, dataFim) {
  const query = dataFim ? `?dataFim=${dataFim}` : '';
  const response = await apiFetch(`${API_CONDOMINIO_BASE_URL}/Condominios/${TENANT_ID}/Unidade/${codigoUnidade}/Morador/${moradorId}${query}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`Falha ao remover morador: ${errorData}`);
  }
  // DELETE geralmente não retorna conteúdo, então apenas confirmamos o sucesso
  return { success: true };
}

/**
 * Edita os dados do proprietário vigente de uma unidade (não troca a pessoa).
 * @param {string} codigoUnidade - O código da unidade.
 * @param {object} proprietarioData - Os dados atualizados do proprietário.
 * @returns {Promise<object>}
 */
export async function updateProprietario(codigoUnidade, proprietarioData) {
  const url = `${API_CONDOMINIO_BASE_URL}/Condominios/${TENANT_ID}/Unidade/${codigoUnidade}/Proprietario`;
  const response = await apiFetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(proprietarioData),
  });

  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`Falha ao atualizar proprietário: ${errorData}`);
  }
  return await response.json();
}

/**
 * Troca o proprietário de uma unidade a partir de uma Data de Início, mantendo o histórico do anterior.
 * @param {string} codigoUnidade - O código da unidade.
 * @param {object} proprietarioData - Os dados do novo proprietário.
 * @param {string} dataInicio - Data de início do novo vínculo (yyyy-MM-dd).
 * @returns {Promise<object>}
 */
export async function trocarProprietario(codigoUnidade, proprietarioData, dataInicio) {
  const url = `${API_CONDOMINIO_BASE_URL}/Condominios/${TENANT_ID}/Unidade/${codigoUnidade}/Proprietario/Troca`;
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ proprietario: proprietarioData, dataInicio }),
  });

  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`Falha ao trocar proprietário: ${errorData}`);
  }
  return await response.json();
}

/**
 * Edita os dados do locatário vigente de uma unidade (não troca a pessoa).
 * @param {string} codigoUnidade - O código da unidade.
 * @param {object} locatarioData - Os dados do locatário.
 * @returns {Promise<object>}
 */
export async function updateLocatario(codigoUnidade, locatarioData) {
  const url = `${API_CONDOMINIO_BASE_URL}/Condominios/${TENANT_ID}/unidade/${codigoUnidade}/locatario`;
  const response = await apiFetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(locatarioData),
  });

  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`Falha ao atualizar locatário: ${errorData}`);
  }
  return await response.json();
}

/**
 * Adiciona ou troca o locatário de uma unidade a partir de uma Data de Início, mantendo o histórico do anterior.
 * @param {string} codigoUnidade - O código da unidade.
 * @param {object} locatarioData - Os dados do novo locatário.
 * @param {string} dataInicio - Data de início do novo vínculo (yyyy-MM-dd).
 * @returns {Promise<object>}
 */
export async function trocarLocatario(codigoUnidade, locatarioData, dataInicio) {
  const url = `${API_CONDOMINIO_BASE_URL}/Condominios/${TENANT_ID}/unidade/${codigoUnidade}/locatario`;
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ locatario: locatarioData, dataInicio }),
  });

  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`Falha ao trocar locatário: ${errorData}`);
  }
  return await response.json();
}

/**
 * Encerra o vínculo do locatário vigente de uma unidade (mantém o histórico).
 * @param {string} codigoUnidade - O código da unidade.
 * @param {string} [dataFim] - Data de fim do vínculo (yyyy-MM-dd). Padrão: hoje.
 * @returns {Promise<object>}
 */
export async function removerLocatario(codigoUnidade, dataFim) {
  const query = dataFim ? `?dataFim=${dataFim}` : '';
  const url = `${API_CONDOMINIO_BASE_URL}/Condominios/${TENANT_ID}/unidade/${codigoUnidade}/locatario${query}`;
  const response = await apiFetch(url, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`Falha ao remover locatário: ${errorData}`);
  }
  // DELETE pode não retornar conteúdo, então retornamos um objeto de sucesso
  return { success: true };
}

/**
 * Busca o histórico completo (proprietários, locatários e moradores) de uma unidade.
 * @param {string} codigoUnidade - O código da unidade.
 * @returns {Promise<object>}
 */
export async function getHistoricoUnidade(codigoUnidade) {
  const url = `${API_CONDOMINIO_BASE_URL}/Condominios/${TENANT_ID}/Unidade/${codigoUnidade}/Historico`;
  const response = await apiFetch(url);

  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`Falha ao buscar histórico da unidade: ${errorData}`);
  }
  return await response.json();
}


/**
 * [SIMULADO] Registra um novo usuário no sistema.
 * ATENÇÃO: Esta função deve chamar um endpoint do SEU backend.
 * O seu backend, então, usará a Management API do Auth0 para criar o usuário.
 * @param {object} userData - Contém { name, email, password }.
 * @returns {Promise<object>}
 */
export async function signupUser(userData) {

  
  const tenantId = window.location.hostname.split('.')[0] || TENANT_ID; // Tenta extrair o tenantId do subdomínio, ou usa o valor padrão

  const signupEndpoint = `${API_CONDOMINIO_BASE_URL}/users/signup`;

  const response = await apiFetch(signupEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Id': tenantId
    },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    // Tenta ler a mensagem de erro do backend.
    const errorData = await response.json().catch(() => ({ message: 'Ocorreu um erro no servidor.' }));
    // Joga um erro com a mensagem vinda do backend (ex: "E-mail já existe").
    throw new Error(errorData.message || "Falha ao criar a conta.");
  }

  logger.transaction('Novo usuário Sign Up', { email: userData.email, tenantId });

  // Retorna a resposta de sucesso do backend.
  return response.json();
}

export async function loginUser(credentials) {
  const response = await apiFetch(`${API_CONDOMINIO_BASE_URL}/users/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Falha no login');
  }

  return await response.json();
}

/**
 * Dispara o e-mail de redefinição de senha do Auth0 (fluxo "Esqueci minha senha").
 * Chama o endpoint público do Auth0 diretamente (não passa por apiFetch/token, e não
 * existe backend nosso no meio) — evita que o usuário fique adivinhando a senha e
 * acabe travando a conta por excesso de tentativas.
 * Por design do Auth0, a resposta não revela se o e-mail existe ou não na base.
 */
export async function requestPasswordReset(email) {
  const url = `https://${AUTH0_DOMAIN}/dbconnections/change_password`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_id: AUTH0_CLIENT_ID,
      email,
      connection: 'Username-Password-Authentication',
    }),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || 'Não foi possível enviar o e-mail de redefinição de senha.');
  }
}

/**
 * Renova o access_token/id_token usando o refresh_token salvo no login por senha.
 * Usa fetch() cru (não apiFetch) de propósito: apiFetch busca o token atual via
 * getAccessToken(), que — quando expirado — chama esta mesma função, causando recursão.
 */
export async function refreshAccessToken(refreshToken) {
  const response = await fetch(`${API_CONDOMINIO_BASE_URL}/users/refresh-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Sessão expirada.');
  }

  return await response.json();
}

// ─── Aprovação de contas ────────────────────────────────────────────────────

export async function getMyAccountStatus() {
  const response = await apiFetch(`${API_CONDOMINIO_BASE_URL}/users/me`);
  if (!response.ok) {
    throw new Error(`Erro ao consultar status da conta: ${response.status}`);
  }
  return response.json();
}

export async function syncAccount({ email, name }) {
  const response = await apiFetch(`${API_CONDOMINIO_BASE_URL}/users/sync`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Id': TENANT_ID,
    },
    body: JSON.stringify({ email, name }),
  });

  if (!response.ok) {
    throw new Error(`Erro ao sincronizar conta: ${response.status}`);
  }
  return response.json();
}

export async function listAccounts(status) {
  const url = status
    ? `${API_CONDOMINIO_BASE_URL}/admin/contas?status=${status}`
    : `${API_CONDOMINIO_BASE_URL}/admin/contas`;
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`Erro ao listar contas: ${response.status}`);
  }
  return response.json();
}

export async function approveAccount(id) {
  const response = await apiFetch(`${API_CONDOMINIO_BASE_URL}/admin/contas/${id}/aprovar`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Erro ao aprovar conta: ${response.status}`);
  }
  return response.json();
}

export async function rejectAccount(id) {
  const response = await apiFetch(`${API_CONDOMINIO_BASE_URL}/admin/contas/${id}/rejeitar`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Erro ao rejeitar conta: ${response.status}`);
  }
  return response.json();
}

export async function unblockAccount(email) {
  const response = await apiFetch(`${API_CONDOMINIO_BASE_URL}/users/desbloquear`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Ocorreu um erro no servidor.' }));
    throw new Error(errorData.message || 'Falha ao desbloquear a conta.');
  }
  return response.json();
}

// ─── Blacklist ────────────────────────────────────────────────────────────────

export async function checkBlacklist(valor, tipo = 'CPF') {
  const paramName = tipo === 'CPF' ? 'cpf' : 'placa'
  const url = `${API_CONDOMINIO_BASE_URL}/blacklist/check?${paramName}=${valor}`
  const response = await apiFetch(url)
  if (!response.ok) {
    throw new Error(`Erro ao verificar blacklist: ${response.status}`)
  }
  return response.json()
}

export async function fetchBlacklist() {
  const url = `${API_CONDOMINIO_BASE_URL}/blacklist`
  const response = await apiFetch(url)
  if (!response.ok) {
    throw new Error(`Erro ao buscar blacklist: ${response.status}`)
  }
  return response.json()
}

export async function addBlacklistEntry(payload) {
  const url = `${API_CONDOMINIO_BASE_URL}/blacklist`
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || `Erro ao adicionar à blacklist: ${response.status}`)
  }
  return response.json()
}

export async function removeBlacklistEntry(id) {
  const url = `${API_CONDOMINIO_BASE_URL}/blacklist/${id}`
  const response = await apiFetch(url, { method: 'DELETE' })
  if (!response.ok) {
    throw new Error(`Erro ao remover da blacklist: ${response.status}`)
  }
  return null
}

export async function resendWhatsapp(autorizacaoId) {
  const url = `${API_CONDOMINIO_BASE_URL}/authorizations/${autorizacaoId}/whatsapp/resend`
  const response = await apiFetch(url, { method: 'POST' })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || `Erro ao reenviar WhatsApp: ${response.status}`)
  }
  return response.json()
}

export async function getWhatsappStatus(autorizacaoId) {
  const url = `${API_CONDOMINIO_BASE_URL}/authorizations/${autorizacaoId}/whatsapp/status`
  const response = await apiFetch(url)
  if (!response.ok) {
    throw new Error(`Erro ao buscar status WhatsApp: ${response.status}`)
  }
  return response.json()
}

// ─── Conciliação de contatos do WhatsApp ────────────────────────────────────

const LOTE_VERIFICACAO_WHATSAPP = 50;

/**
 * Lê a agenda de contatos da instância WhatsApp do condomínio (somente administradores).
 * @returns {Promise<{ total: number, semTelefone: number, contatos: Array<{ telefone: string, nome: string|null, pushName: string|null }> }>}
 */
/**
 * Estado da conexão do WhatsApp do condomínio (somente administradores).
 * @returns {Promise<{ estado: 'conectado'|'conectando'|'desconectado'|'nao_configurado' }>}
 */
export async function fetchConexaoWhatsapp() {
  const url = `${API_ENCOMENDA_BASE_URL}/encomendas/${TENANT_ID}/whatsapp/conexao`;
  return fetchWithRetry(url, {}, 1, 2000);
}

/**
 * Gera um QR code de pareamento do WhatsApp do condomínio. O QR code expira em segundos.
 * @returns {Promise<{ estado: string, qrCodeBase64: string|null, codigoPareamento: string|null }>}
 */
export async function gerarQrCodeWhatsapp() {
  const response = await apiFetch(`${API_ENCOMENDA_BASE_URL}/encomendas/${TENANT_ID}/whatsapp/conexao/qrcode`, { method: 'POST' });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Erro ao gerar o QR code do WhatsApp: ${response.status}`);
  }
  return response.json();
}

export async function fetchContatosWhatsapp() {
  const url = `${API_ENCOMENDA_BASE_URL}/encomendas/${TENANT_ID}/whatsapp/contatos`;
  return fetchWithRetry(url, {}, 2, 3000);
}

/**
 * Verifica quais telefones têm conta no WhatsApp, em lotes de até 50 com
 * intervalo entre eles para não sobrecarregar o número do condomínio.
 * @param {string[]} telefones - Telefones canônicos (com DDI).
 * @param {{ intervaloMs?: number }} [opcoes]
 * @returns {Promise<Array<{ telefone: string, existe: boolean }>>}
 */
export async function verificarNumerosWhatsapp(telefones, { intervaloMs = 1500 } = {}) {
  const url = `${API_ENCOMENDA_BASE_URL}/encomendas/${TENANT_ID}/whatsapp/verificar-numeros`;
  const resultados = [];

  for (let i = 0; i < telefones.length; i += LOTE_VERIFICACAO_WHATSAPP) {
    if (i > 0 && intervaloMs > 0) await new Promise((resolve) => setTimeout(resolve, intervaloMs));

    const response = await apiFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ telefones: telefones.slice(i, i + LOTE_VERIFICACAO_WHATSAPP) }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Erro ao verificar números no WhatsApp: ${response.status}`);
    }
    const data = await response.json();
    resultados.push(...(data.resultados ?? []));
  }

  return resultados;
}

/**
 * Recupera os dados da pré-autorização a partir de um token seguro.
 * @param {string} token - O token seguro recebido pelo visitante.
 * @returns {Promise<object>} - Os dados da pré-autorização.
 */
export async function getPreRegistration(token) {
  const url = `${API_CONDOMINIO_BASE_URL}/authorizations/pre-register?token=${encodeURIComponent(token)}`;
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`Erro ao buscar pré-autorização: ${response.status}`);
  }
  return response.json();
}

/**
 * Conclui o cadastro do visitante vinculando seus dados e selfie à pré-autorização.
 * @param {string} token - O token de cadastro.
 * @param {object} visitorData - Os dados preenchidos (nome, CPF, telefone, selfie_b64).
 * @returns {Promise<object>} - A resposta com o QR Code e status.
 */
export async function completeRegistration(token, visitorData) {
  const url = `${API_CONDOMINIO_BASE_URL}/authorizations/complete-registration`;
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, ...visitorData }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Erro ao completar cadastro: ${response.status}`);
  }
  return response.json();
}

/**
 * Cria uma solicitação de liberação remota (anúncio) a partir da portaria.
 * @param {object} announcementData - Dados do anúncio (unit_id, nome, CPF, motivo).
 * @returns {Promise<object>} - Dados do anúncio criado.
 */
export async function createAnnouncement(announcementData) {
  const url = `${API_CONDOMINIO_BASE_URL}/announcements`;
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(announcementData),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Erro ao criar anúncio: ${response.status}`);
  }
  return response.json();
}

/**
 * Consulta o status atual de um anúncio pendente (liberação remota).
 * @param {string} announcementId - O ID do anúncio.
 * @returns {Promise<object>} - O status atual do anúncio.
 */
export async function getAnnouncementStatus(announcementId) {
  const url = `${API_CONDOMINIO_BASE_URL}/announcements/${announcementId}/status`;
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`Erro ao consultar status do anúncio: ${response.status}`);
  }
  return response.json();
}
